import type { Document, DocumentVersion } from "@models";
import { useCallback, useEffect, useRef } from "react";
import {
  useLocation,
  useSearchParams,
  type SetURLSearchParams,
} from "react-router-dom";
import type { SimpleEditorSnapshotVersion } from "src/tiptap/components/tiptap-templates/simple/version-history";
import { getBackendDocumentVersionForTiptapVersion } from "./tiptapVersionCardMappings";

type UseSyncTiptapEditorRouteParams = {
  activeComparisonVersion: number | null;
  document?: Document | null;
  isRouteSelectionPending: boolean;
  latestTiptapVersion: number | null;
  searchParams: URLSearchParams;
  selectedTiptapVersion: number | null;
  setSearchParams: SetURLSearchParams;
};

type UseTiptapEditorRouteSelectionParams = {
  compareVersion: (version: number) => void;
  initialComparisonVersion: number | null;
  initialSelectedVersion: number | null;
  isVersionHistorySynced: boolean;
  selectVersion: (version: number) => void;
  versions: SimpleEditorSnapshotVersion[];
  versionsRefreshKey: string;
};

const parseTiptapVersionSearchParam = (value: string) => {
  const parsedVersion = Number(value);

  return Number.isInteger(parsedVersion) && parsedVersion >= 0
    ? parsedVersion
    : null;
};

const getRouteSelectionKey = ({
  comparisonVersion,
  selectedVersion,
  versionsRefreshKey,
}: {
  comparisonVersion: number | null;
  selectedVersion: number | null;
  versionsRefreshKey: string;
}) => {
  const routeVersion = comparisonVersion ?? selectedVersion;

  if (routeVersion === null) {
    return null;
  }

  return [
    versionsRefreshKey,
    comparisonVersion === null ? "preview" : "comparison",
    routeVersion,
  ].join(":");
};

export const useTiptapEditorRouteParams = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tiptapVersionSearchParam = searchParams.get("tiptapVersion");
  const initialSelectedVersion =
    tiptapVersionSearchParam === null
      ? null
      : parseTiptapVersionSearchParam(tiptapVersionSearchParam);
  const initialComparisonVersion =
    searchParams.get("diff") === "true" ? initialSelectedVersion : null;
  const getCommentThreadLinkUrl = useCallback((threadId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("commentThreadId", threadId);
    return url.toString();
  }, []);

  return {
    agentThreadId: searchParams.get("agentThreadId") ?? "",
    commentThreadId: searchParams.get("commentThreadId") ?? "",
    getCommentThreadLinkUrl,
    initialComparisonVersion,
    initialSelectedVersion,
    navigationKey: location.key,
    searchParams,
    setSearchParams,
    tiptapSuggestionId: searchParams.get("tiptapSuggestionId") ?? "",
  };
};

export const useTiptapEditorRouteSelection = ({
  compareVersion,
  initialComparisonVersion,
  initialSelectedVersion,
  isVersionHistorySynced,
  selectVersion,
  versions,
  versionsRefreshKey,
}: UseTiptapEditorRouteSelectionParams) => {
  const routeSelectionRef = useRef({
    comparisonVersion: initialComparisonVersion,
    selectedVersion: initialSelectedVersion,
    versionsRefreshKey,
  });
  const appliedRouteSelectionKeyRef = useRef<string | null>(null);
  const nextRouteSelectionKey = getRouteSelectionKey({
    comparisonVersion: initialComparisonVersion,
    selectedVersion: initialSelectedVersion,
    versionsRefreshKey,
  });
  const currentRouteSelectionKey = getRouteSelectionKey(
    routeSelectionRef.current
  );

  if (currentRouteSelectionKey !== nextRouteSelectionKey) {
    routeSelectionRef.current = {
      comparisonVersion: initialComparisonVersion,
      selectedVersion: initialSelectedVersion,
      versionsRefreshKey,
    };
    appliedRouteSelectionKeyRef.current = null;
  }

  const routeSelectedVersion = routeSelectionRef.current.selectedVersion;
  const routeComparisonVersion = routeSelectionRef.current.comparisonVersion;
  const routeVersion = routeComparisonVersion ?? routeSelectedVersion;
  const routeSelectionKey = getRouteSelectionKey({
    comparisonVersion: routeComparisonVersion,
    selectedVersion: routeSelectedVersion,
    versionsRefreshKey,
  });
  const isRouteSelectionPending =
    routeSelectionKey !== null &&
    appliedRouteSelectionKeyRef.current !== routeSelectionKey &&
    (!isVersionHistorySynced ||
      versions.length === 0 ||
      versions.some((version) => version.version === routeVersion));

  useEffect(() => {
    if (
      routeVersion === null ||
      routeSelectionKey === null ||
      !isVersionHistorySynced ||
      versions.length === 0
    ) {
      return;
    }

    if (appliedRouteSelectionKeyRef.current === routeSelectionKey) {
      return;
    }

    const hasRouteVersion = versions.some(
      (version) => version.version === routeVersion
    );

    if (!hasRouteVersion) {
      appliedRouteSelectionKeyRef.current = routeSelectionKey;
      return;
    }

    appliedRouteSelectionKeyRef.current = routeSelectionKey;

    if (routeComparisonVersion !== null) {
      compareVersion(routeVersion);
      return;
    }

    selectVersion(routeVersion);
  }, [
    compareVersion,
    isVersionHistorySynced,
    routeComparisonVersion,
    routeSelectionKey,
    routeVersion,
    selectVersion,
    versions,
  ]);

  return { isRouteSelectionPending };
};

export const useSyncTiptapEditorRoute = ({
  activeComparisonVersion,
  document,
  isRouteSelectionPending,
  latestTiptapVersion,
  searchParams,
  selectedTiptapVersion,
  setSearchParams,
}: UseSyncTiptapEditorRouteParams) => {
  const routeSelectedTiptapVersion =
    activeComparisonVersion ?? selectedTiptapVersion;
  const previousSelectedTiptapVersionRef = useRef(
    routeSelectedTiptapVersion
  );

  useEffect(() => {
    if (
      isRouteSelectionPending ||
      !document ||
      document.versions.length === 0 ||
      latestTiptapVersion === null
    ) {
      return;
    }

    const previousSelectedTiptapVersion =
      previousSelectedTiptapVersionRef.current;
    previousSelectedTiptapVersionRef.current = routeSelectedTiptapVersion;

    if (routeSelectedTiptapVersion === null) {
      if (previousSelectedTiptapVersion === null) {
        return;
      }

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("v", document.versions[0].id);
      nextSearchParams.delete("tiptapVersion");
      nextSearchParams.delete("diff");

      if (nextSearchParams.toString() === searchParams.toString()) {
        return;
      }

      setSearchParams(nextSearchParams, { replace: true });
      return;
    }

    const routeSelectedDocVersion: DocumentVersion =
      getBackendDocumentVersionForTiptapVersion({
        document,
        latestTiptapVersion,
        selectedTiptapVersion: routeSelectedTiptapVersion,
      }) ?? document.versions[0];
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("v", routeSelectedDocVersion.id);
    nextSearchParams.set("tiptapVersion", String(routeSelectedTiptapVersion));

    if (activeComparisonVersion === null) {
      nextSearchParams.delete("diff");
    } else {
      nextSearchParams.set("diff", "true");
    }

    if (nextSearchParams.toString() === searchParams.toString()) {
      return;
    }

    setSearchParams(nextSearchParams, { replace: true });
  }, [
    activeComparisonVersion,
    document,
    isRouteSelectionPending,
    latestTiptapVersion,
    routeSelectedTiptapVersion,
    searchParams,
    setSearchParams,
  ]);
};
